import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { employeeApi, goodiesApi, getErrorMessage } from '../utils/api';
import { isAuthenticated } from '../utils/auth';
import { showToast } from '../utils/toast';
import { processLocation } from '../utils/locationUtils';
import * as XLSX from 'xlsx';
import { ROUTES } from '../constants/app';

export function useDashboard() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [goodies, setGoodies] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [modalGoodieStatuses, setModalGoodieStatuses] = useState({});
  const [modalGoodieSizes, setModalGoodieSizes] = useState({});
  const [modalSizeChangeReasons, setModalSizeChangeReasons] = useState({});
  const [editingSizes, setEditingSizes] = useState({});
  const [savingSizes, setSavingSizes] = useState({});
  const [markingCompleted, setMarkingCompleted] = useState({});
  
  // Statistics modal state
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false);

  // Get goodie types from API data
  const getGoodieTypes = useCallback(() => {
    return goodies.map(goodie => {
      // Capitalize first letter for display
      return goodie.name ? goodie.name.charAt(0).toUpperCase() + goodie.name.slice(1) : goodie;
    }).sort();
  }, [goodies]);

  // Get unique locations from employees data
  const getUniqueLocations = useCallback(() => {
    const locations = employees
      .map(emp => processLocation(emp.location))
      .filter(location => location && location.trim() !== '')
      .filter((location, index, arr) => arr.indexOf(location) === index)
      .sort();
    return locations;
  }, [employees]);

  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      
      // Check if user is authenticated
      if (!isAuthenticated()) {
        setError('Authentication required. Please login first.');
        navigate(ROUTES.HOME);
        return;
      }
      
      const response = await employeeApi.getAllDetails();
      
      if (response.success && response.data) {
        setEmployees(response.data);
        setFilteredEmployees(response.data);
      } else {
        setError('Failed to fetch employees');
      }
    } catch (err) {
      setError(getErrorMessage(err));
      console.error('Error fetching employees:', err);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const filterEmployees = useCallback(() => {
    let filtered = [...employees];

    // Search by ID or name - using the actual field names from API
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(emp => {
        // Handle different possible field name variations
        const empId = emp.empId || emp.emp_id || emp.id || '';
        const firstName = emp.firstName || emp.first_name || '';
        const lastName = emp.lastName || emp.last_name || '';
        const fullName = `${firstName} ${lastName}`.trim();
        
        return empId.toString().includes(search) ||
               firstName.toLowerCase().includes(search) ||
               lastName.toLowerCase().includes(search) ||
               fullName.toLowerCase().includes(search);
      });
    }

    // Filter by location
    if (selectedLocation) {
      filtered = filtered.filter(emp => 
        processLocation(emp.location).toLowerCase() === selectedLocation.toLowerCase()
      );
    }

    // Sort by employee ID (ascending)
    filtered.sort((a, b) => {
      const idA = a.empId || a.emp_id || 0;
      const idB = b.empId || b.emp_id || 0;
      return Number(idA) - Number(idB);
    });

    setFilteredEmployees(filtered);
  }, [employees, searchTerm, selectedLocation]);

  const fetchGoodies = useCallback(async () => {
    try {
      // Check if user is authenticated
      if (!isAuthenticated()) {
        setError('Authentication required. Please login first.');
        navigate(ROUTES.HOME);
        return;
      }
      
      const response = await goodiesApi.getAll();
      
      if (response.success && response.data) {
        setGoodies(response.data);
      } else {
        console.warn('Goodies API returned no data, using empty array');
        setGoodies([]);
      }
    } catch (err) {
      console.error('Error fetching goodies:', err);
      console.warn('Falling back to empty goodies array due to API error');
      // Don't set error for goodies - just use empty array as fallback
      setGoodies([]);
    }
  }, [navigate]);

  const getCollectionStats = useCallback(() => {
    const goodieTypes = getGoodieTypes();
    // Initialize stats dynamically based on goodie types
    const stats = {};
    goodieTypes.forEach(goodieType => {
      stats[goodieType] = { delivered: 0, returned: 0, notDelivered: 0, total: employees.length };
    });

    employees.forEach(emp => {
      if (emp.goodies && Array.isArray(emp.goodies)) {
        // Check each goodie type
        goodieTypes.forEach(goodieType => {
          // Find the goodie from the master goodies list by matching the display name
          const goodieFromMasterList = goodies.find(g => 
            g.name && g.name.charAt(0).toUpperCase() + g.name.slice(1) === goodieType
          );
          
          // Find the goodie in the employee's goodies array by ID
          let employeeGoodie = null;
          if (goodieFromMasterList) {
            employeeGoodie = emp.goodies.find(g => g.id === goodieFromMasterList.id);
          }
          
          if (employeeGoodie) {
            if (employeeGoodie.received) {
              stats[goodieType].delivered++;
            } else {
              stats[goodieType].notDelivered++;
            }
          } else {
            stats[goodieType].notDelivered++;
          }
        });
      } else {
        goodieTypes.forEach(goodieType => {
          stats[goodieType].notDelivered++;
        });
      }
    });

    return stats;
  }, [employees, getGoodieTypes, goodies]);

  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedLocation('');
    setCurrentPage(1);
  }, []);

  const exportToExcel = useCallback(() => {
    const goodieTypes = getGoodieTypes();
    // Prepare data for export
    const exportData = filteredEmployees.map(employee => {
      // Handle different possible field name variations
      const empId = employee.empId || employee.emp_id || employee.id || '';
      const firstName = employee.firstName || employee.first_name || '';
      const lastName = employee.lastName || employee.last_name || '';
      
      const row = {
        'Employee ID': empId,
        'First Name': firstName,
        'Last Name': lastName,
        'Full Name': `${firstName} ${lastName}`.trim(),
        'Location': processLocation(employee.location) || 'Remote',
        'Designation': employee.designation || 'Not specified',
        'Email': employee.email || 'Not specified',
        'Phone': employee.phone || 'Not specified',
      };

      // Add goodie status columns
      goodieTypes.forEach(goodie => {
        // Find the goodie from the master goodies list by matching the display name
        const goodieFromMasterList = goodies.find(g =>
          g.name && g.name.charAt(0).toUpperCase() + g.name.slice(1) === goodie
        );

        // Check if employee has collected this goodie by matching the goodie ID
        let status = 'not-delivered';
        let size = 'Not specified';

        if (employee.goodies && Array.isArray(employee.goodies) && goodieFromMasterList) {
          const employeeGoodie = employee.goodies.find(g => g.id === goodieFromMasterList.id);
          if (employeeGoodie) {
            status = employeeGoodie.received === true ? 'delivered' : 'not-delivered';
            // Get size information if available
            if (employeeGoodie.size) {
              size = employeeGoodie.size;
            }
          }
        }

        row[`${goodie} Status`] = status === 'delivered' ? 'Collected' : 'Not Collected';

        // Add size information for Kurta and Shoe
        if (goodie === 'Kurta' || goodie === 'Shoe') {
          row[`${goodie} Size`] = size;
        }
      });

      return row;
    });

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Set column widths
    const colWidths = [
      { wch: 12 }, // Employee ID
      { wch: 15 }, // First Name
      { wch: 15 }, // Last Name
      { wch: 25 }, // Full Name
      { wch: 15 }, // Location
      { wch: 20 }, // Designation
      { wch: 25 }, // Email
      { wch: 15 }, // Phone
      { wch: 15 }, // ID Card Status
      { wch: 15 }, // Shoe Status
      { wch: 12 }, // Shoe Size
      { wch: 15 }, // Kurta Status
      { wch: 12 }, // Kurta Size
    ];
    ws['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Employee Data');

    // Generate filename with current date and filter info
    const currentDate = new Date().toISOString().split('T')[0];
    let filename = `employee-data-${currentDate}`;
    
    if (searchTerm) {
      filename += `-search-${searchTerm.replace(/[^a-zA-Z0-9]/g, '')}`;
    }
    if (selectedLocation) {
      filename += `-location-${selectedLocation.toLowerCase().replace(/\s+/g, '-')}`;
    }
    
    filename += '.xlsx';

    // Save the file
    XLSX.writeFile(wb, filename);
  }, [filteredEmployees, searchTerm, selectedLocation, getGoodieTypes, goodies]);

  // Modal handlers
  const openModal = useCallback((employee) => {
    const goodieTypes = getGoodieTypes();
    setSelectedEmployee(employee);
    const currentStatuses = {};
    const currentSizes = {};
    
    goodieTypes.forEach(goodieType => {
      // Find the goodie from the master goodies list by matching the display name
      const goodieFromMasterList = goodies.find(g => 
        g.name && g.name.charAt(0).toUpperCase() + g.name.slice(1) === goodieType
      );
      
      // Find the goodie in the employee's goodies array by ID
      let employeeGoodie = null;
      if (goodieFromMasterList && employee.goodies && Array.isArray(employee.goodies)) {
        employeeGoodie = employee.goodies.find(g => g.id === goodieFromMasterList.id);
      }
      
      // Set status based on goodie data
      if (employeeGoodie) {
        currentStatuses[goodieType] = employeeGoodie.received ? 'delivered' : 'not-delivered';
        // Load size from goodie data if available
        currentSizes[goodieType] = employeeGoodie.size || '';
      } else {
        currentStatuses[goodieType] = 'not-delivered';
        currentSizes[goodieType] = '';
      }
    });
    
    setModalGoodieStatuses(currentStatuses);
    setModalGoodieSizes(currentSizes);
    setEditingSizes({});
    setIsModalOpen(true);
  }, [getGoodieTypes, goodies]);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedEmployee(null);
    setModalGoodieStatuses({});
    setModalGoodieSizes({});
    setModalSizeChangeReasons({});
    setEditingSizes({});
    setSavingSizes({});
    setMarkingCompleted({});
  }, []);

  const handleModalStatusChange = useCallback((goodieName, newStatus) => {
    setModalGoodieStatuses(prev => ({
      ...prev,
      [goodieName]: newStatus
    }));
  }, []);

  const handleModalSizeChange = useCallback((goodieName, newSize) => {
    setModalGoodieSizes(prev => ({
      ...prev,
      [goodieName]: newSize
    }));
  }, []);

  const handleSizeChangeReasonChange = useCallback((goodieName, reason) => {
    setModalSizeChangeReasons(prev => ({
      ...prev,
      [goodieName]: reason
    }));
  }, []);

  const startEditingSize = useCallback((goodieName) => {
    setEditingSizes(prev => ({
      ...prev,
      [goodieName]: true
    }));
  }, []);

  const cancelEditingSize = useCallback((goodieName) => {
    setEditingSizes(prev => ({
      ...prev,
      [goodieName]: false
    }));
  }, []);

  const saveSizeChange = useCallback(async (goodieName) => {
    if (!selectedEmployee) return;
    
    const newSize = modalGoodieSizes[goodieName];
    const reason = modalSizeChangeReasons[goodieName];
    
    // Validate required fields
    if (!newSize || !newSize.trim()) {
      showToast.warning('Please enter a size');
      return;
    }
    
    if (!reason || !reason.trim()) {
      showToast.warning('Please provide a reason for the size change');
      return;
    }
    
    // Find the goodie ID from the employee's goodies
    const employeeGoodie = selectedEmployee.goodies?.find(g => g.name === goodieName.toLowerCase());
    if (!employeeGoodie) {
      showToast.error('Goodie not found for this employee');
      return;
    }
    
    const requestData = {
      goodies: [
        {
          goodie_id: employeeGoodie.id,
          reason: reason,
          size: newSize
        }
      ]
    };
    
    try {
      // Set loading state
      setSavingSizes(prev => ({
        ...prev,
        [goodieName]: true
      }));
      
      const empId = selectedEmployee.empId || selectedEmployee.emp_id;
      const response = await employeeApi.returnGoodies(empId, requestData);
      
      if (response.success) {
        // Update local state
        const updatedEmployees = employees.map(emp => {
          const currentEmpId = emp.empId || emp.emp_id;
          if (currentEmpId === empId) {
            return {
              ...emp,
              goodies: emp.goodies?.map(goodie => 
                goodie.name === goodieName.toLowerCase() 
                  ? { ...goodie, size: newSize }
                  : goodie
              ) || []
            };
          }
          return emp;
        });
        
        setEmployees(updatedEmployees);
        
        // Update selected employee
        setSelectedEmployee(prev => ({
          ...prev,
          goodies: prev.goodies?.map(goodie => 
            goodie.name === goodieName.toLowerCase() 
              ? { ...goodie, size: newSize }
              : goodie
          ) || []
        }));
        
        // Clear the reason field
        setModalSizeChangeReasons(prev => ({
          ...prev,
          [goodieName]: ''
        }));
        
        // Stop editing
        setEditingSizes(prev => ({
          ...prev,
          [goodieName]: false
        }));
        
        showToast.success('Size updated successfully');
      } else {
        showToast.error('Failed to update size');
      }
    } catch (err) {
      console.error('Error updating size:', err);
      showToast.error(`Error updating size: ${err.message}`);
    } finally {
      // Clear loading state
      setSavingSizes(prev => ({
        ...prev,
        [goodieName]: false
      }));
    }
  }, [selectedEmployee, modalGoodieSizes, modalSizeChangeReasons, employees]);

  const markGoodieAsCollected = useCallback(async (goodieId) => {
    if (!selectedEmployee) return;
    
    // Find the goodie from the goodies list to get the name for UI updates
    const goodieFromList = goodies.find(g => g.id === goodieId);
    if (!goodieFromList) {
      showToast.error('Goodie not found in the goodies list');
      return;
    }
    
    const goodieName = goodieFromList.name;
    
    const requestData = {
      goodies: [goodieId]
    };
    
    try {
      // Set loading state
      setMarkingCompleted(prev => ({
        ...prev,
        [goodieId]: true
      }));
      
      const empId = selectedEmployee.empId || selectedEmployee.emp_id;
      const response = await employeeApi.completeGoodies(empId, requestData);
      
      if (response.success) {
        // Update local state - mark goodie as delivered/completed
        const updatedEmployees = employees.map(emp => {
          const currentEmpId = emp.empId || emp.emp_id;
          if (currentEmpId === empId) {
            return {
              ...emp,
              goodies: emp.goodies?.map(goodie => 
                goodie.name === goodieName.toLowerCase() 
                  ? { ...goodie, received: true }
                  : goodie
              ) || []
            };
          }
          return emp;
        });
        
        setEmployees(updatedEmployees);
        
        // Update selected employee
        setSelectedEmployee(prev => ({
          ...prev,
          goodies: prev.goodies?.map(goodie => 
            goodie.name === goodieName.toLowerCase() 
              ? { ...goodie, received: true }
              : goodie
          ) || []
        }));
        
        // Update modal status
        setModalGoodieStatuses(prev => ({
          ...prev,
          [goodieName]: 'delivered'
        }));
        
        showToast.success(`${goodieName} marked as completed successfully`);
      } else {
        showToast.error('Failed to mark goodie as completed');
      }
    } catch (err) {
      console.error('Error marking goodie as completed:', err);
      showToast.error(`Error marking goodie as completed: ${err.message}`);
    } finally {
      // Clear loading state
      setMarkingCompleted(prev => ({
        ...prev,
        [goodieId]: false
      }));
    }
  }, [selectedEmployee, goodies, employees]);

  const saveModalChanges = useCallback(async () => {
    if (!selectedEmployee) return;
    
    // Update both statuses and sizes
    const updateData = {
      goodies: modalGoodieStatuses,
      goodieSizes: modalGoodieSizes
    };
    
    try {
      const response = await employeeApi.updateGoodies(selectedEmployee.empId, updateData);
      
      if (response.success) {
        // Update local state
        setEmployees(prevEmployees =>
          prevEmployees.map(emp =>
            emp.empId === selectedEmployee.empId
              ? {
                  ...emp,
                  goodies: {
                    ...emp.goodies,
                    ...modalGoodieStatuses
                  },
                  goodieSizes: {
                    ...emp.goodieSizes,
                    ...modalGoodieSizes
                  }
                }
              : emp
          )
        );
        closeModal();
        showToast.success('Goodie information updated successfully');
      } else {
        showToast.error('Failed to update goodie information');
      }
    } catch (err) {
      console.error('Error updating goodie information:', err);
      showToast.error('Error updating goodie information');
    }
  }, [selectedEmployee, modalGoodieStatuses, modalGoodieSizes, closeModal]);

  // Pagination handlers
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  }, []);

  // Effects
  useEffect(() => {
    const initializeDashboard = async () => {
      await fetchGoodies();
      await fetchEmployees();
    };
    
    initializeDashboard();
  }, [fetchGoodies, fetchEmployees]);

  useEffect(() => {
    filterEmployees();
  }, [filterEmployees]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedLocation]);

  // Computed values
  const goodieTypes = getGoodieTypes();
  const locations = getUniqueLocations();
  const stats = getCollectionStats();
  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentEmployees = filteredEmployees.slice(startIndex, startIndex + itemsPerPage);

  return {
    // State
    employees,
    goodies,
    filteredEmployees,
    loading,
    error,
    searchTerm,
    selectedLocation,
    currentPage,
    itemsPerPage,
    isModalOpen,
    selectedEmployee,
    modalGoodieStatuses,
    modalGoodieSizes,
    modalSizeChangeReasons,
    editingSizes,
    savingSizes,
    markingCompleted,
    isStatsModalOpen,
    
    // Computed values
    goodieTypes,
    locations,
    stats,
    totalPages,
    currentEmployees,
    
    // Setters
    setSearchTerm,
    setSelectedLocation,
    setIsStatsModalOpen,
    
    // Handlers
    fetchEmployees,
    fetchGoodies,
    clearFilters,
    exportToExcel,
    openModal,
    closeModal,
    handleModalStatusChange,
    handleModalSizeChange,
    handleSizeChangeReasonChange,
    startEditingSize,
    cancelEditingSize,
    saveSizeChange,
    markGoodieAsCollected,
    saveModalChanges,
    handlePageChange,
    handleItemsPerPageChange
  };
}
